import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuth } from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import CustomLoading from '../../components/customLoading/customLoading';
import ErrorMessage from '../../components/errorMessage/errorMessage';
import { useBackEndRequests } from './lib/backEndRequests';
import styles from './Login.module.scss';
import { IRepresentante, IRepresentanteReceber } from '../../interfaces/IRepresentante';
import { AxiosError } from 'axios';
import { isCPForCNPJ } from '../../utils/formatCNPJOrCPF';
import { BrButton } from '@govbr-ds/react-components';

function Login() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { getRepresentantes } = useBackEndRequests();
  const [errorStatus, setErrorStatus] = useState<string>('');

  const {
    data: representantesData,
    isError: isRepresenentatesError,
    error: representantesError,
  } = useQuery<IRepresentanteReceber, AxiosError>({
    queryKey: ['representantes'],
    queryFn: () => getRepresentantes(1),
    refetchOnWindowFocus: false,
    retryDelay: 100,
    enabled: auth.isAuthenticated && auth.isLoading === false,
  });

  useEffect(() => {
    if (auth.isAuthenticated && auth.user && representantesData) {
      const cpfCnpj = (auth.user.cpfcnpj as string) || representantesData.content[0].id;
      sessionStorage.setItem('userCpfCnpj', cpfCnpj.toString());
      sessionStorage.setItem('usuarioCpf', cpfCnpj.toString());
      navigate('/');
    } else if (auth.isLoading === false && auth.isAuthenticated === false) {
      auth.login();
    }
  }, [auth.isAuthenticated, auth.isLoading, representantesData]);

  useEffect(() => {
    if (representantesError) {
      setErrorStatus(representantesError?.status?.toString() || '');
    } else {
      setErrorStatus('');
    }
  }, [representantesError, representantesData]);

  let content;

  const errorDescription = () => {
    if (errorStatus === '404') {
      return (
        <>
          O {isCPForCNPJ((auth?.user?.profile?.cpfcnpj as string) ?? '00000000000')} informado não está cadastrado no
          sistema. Por favor, verifique com a Agência Nacional de Mineração (ANM) se o cadastro está correto. Após o
          cadastro, <b>aguarde 1 dia para a validação dos dados.</b> Para mais informações, clique no botão abaixo e
          acesse o sistema da ANM.
        </>
      );
    } else {
      return 'Ocorreu um erro tentando recuperar seus dados. Tente novamente clicando no botão abaixo. Caso o erro persista, favor entrar em contato com o suporte da ANM pelo e-mail corfem@anm.gov.br.';
    }
  };

  const onClickError = () => {
    if (errorStatus === '404') {
      return () => {
        window.location.href = 'https://www.gov.br/anm/pt-br/assuntos/exploracao-mineral/Cadastro-para-Requerimentos';
      };
    } else {
      return () => navigate(0);
    }
  };

  const errorTitle = () => {
    if (errorStatus === '404') {
      return 'Usuário não encontrado';
    } else {
      return 'Erro ao recuperar seus dados';
    }
  };

  const buttonError = () => {
    if (errorStatus === '404') {
      return 'Acessar ANM';
    } else {
      return 'Tentar novamente';
    }
  };

  if (!auth.isAuthenticated) {
    content = (
      <div className={styles.container}>
        <CustomLoading label='Redirecionando para a tela de Login. Clique no botão abaixo para ser redirecionado imediatamente.'>
          <BrButton secondary onClick={() => auth.login()}>
            Login
          </BrButton>
        </CustomLoading>
      </div>
    );
  } else if (isRepresenentatesError) {
    content = (
      <div className={styles.container}>
        <ErrorMessage
          errorMessage={representantesError?.message || ''}
          errorTitle={errorTitle()}
          errorDescription={errorDescription()}
          onClickFunction={onClickError()}
          buttonMessage={buttonError()}
          noBorder
        />
      </div>
    );
  } else {
    content = (
      <div className={styles.container}>
        <CustomLoading label='Finalizando o seu Login' />
      </div>
    );
  }

  return <>{content}</>;
}

export default Login;
